export const READER_DISPLAY_SETTINGS_VERSION = 1;

export const READER_FONT_SIZE_MIN = 12;
export const READER_FONT_SIZE_MAX = 24;
export const READER_FONT_SIZE_STEP = 2;
export const READER_FONT_SIZE_DEFAULT = 16;

export const READER_LINE_HEIGHT_MIN = 1.4;
export const READER_LINE_HEIGHT_MAX = 2.2;
export const READER_LINE_HEIGHT_STEP = 0.05;
export const READER_LINE_HEIGHT_DEFAULT = 1.75;

export const READER_THEME_IDS = ['light', 'soft', 'sepia', 'dark', 'night'] as const;

export type ReaderThemeId = (typeof READER_THEME_IDS)[number];

export type ReaderTone = 'light' | 'dark';

export type ReaderTheme = {
  id: ReaderThemeId;
  label: string;
  background: string;
  text: string;
  surface: string;
  muted: string;
  tone: ReaderTone;
};

export const READER_THEMES: Record<ReaderThemeId, ReaderTheme> = {
  light: {
    id: 'light',
    label: 'Светлая',
    background: '#FFFFFF',
    text: '#111827',
    surface: '#F7F7F7',
    muted: '#6B7280',
    tone: 'light',
  },
  soft: {
    id: 'soft',
    label: 'Мягкая',
    background: '#F9FAFB',
    text: '#1F2937',
    surface: '#FFFFFF',
    muted: '#6B7280',
    tone: 'light',
  },
  sepia: {
    id: 'sepia',
    label: 'Сепия',
    background: '#FEF3C7',
    text: '#1F2937',
    surface: '#FFFBEB',
    muted: '#78716C',
    tone: 'light',
  },
  dark: {
    id: 'dark',
    label: 'Тёмная',
    background: '#1F2937',
    text: '#FFFFFF',
    surface: '#111827',
    muted: '#9CA3AF',
    tone: 'dark',
  },
  night: {
    id: 'night',
    label: 'Ночная',
    background: '#0F172A',
    text: '#FFFFFF',
    surface: '#1E293B',
    muted: '#94A3B8',
    tone: 'dark',
  },
};

const KNOWN_SETTINGS_KEYS = new Set(['version', 'theme', 'fontSize', 'lineHeight']);

export type ReaderDisplaySettings = {
  version: number;
  theme: ReaderThemeId;
  fontSize: number;
  lineHeight: number;
  /** Unknown keys kept so future reader params survive a round-trip. */
  extra: Record<string, unknown>;
};

export const DEFAULT_READER_DISPLAY_SETTINGS: ReaderDisplaySettings = {
  version: READER_DISPLAY_SETTINGS_VERSION,
  theme: 'light',
  fontSize: READER_FONT_SIZE_DEFAULT,
  lineHeight: READER_LINE_HEIGHT_DEFAULT,
  extra: {},
};

export function getReaderTheme(themeId: ReaderThemeId): ReaderTheme {
  return READER_THEMES[themeId];
}

function isReaderThemeId(value: unknown): value is ReaderThemeId {
  return typeof value === 'string' && (READER_THEME_IDS as readonly string[]).includes(value);
}

export function snapReaderFontSize(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  if (value < READER_FONT_SIZE_MIN || value > READER_FONT_SIZE_MAX) return null;
  const snapped = Math.round(value / READER_FONT_SIZE_STEP) * READER_FONT_SIZE_STEP;
  return Math.min(READER_FONT_SIZE_MAX, Math.max(READER_FONT_SIZE_MIN, snapped));
}

export function snapReaderLineHeight(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  if (value < READER_LINE_HEIGHT_MIN || value > READER_LINE_HEIGHT_MAX) return null;
  const steps = Math.round(value / READER_LINE_HEIGHT_STEP);
  const snapped = Math.round(steps * READER_LINE_HEIGHT_STEP * 100) / 100;
  return Math.min(READER_LINE_HEIGHT_MAX, Math.max(READER_LINE_HEIGHT_MIN, snapped));
}

export function formatReaderLineHeight(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

export function parseReaderDisplaySettings(value: unknown): ReaderDisplaySettings | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const version = record.version;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;
  if (!isReaderThemeId(record.theme)) return null;
  if (typeof record.fontSize !== 'number' || typeof record.lineHeight !== 'number') return null;

  const fontSize = snapReaderFontSize(record.fontSize);
  const lineHeight = snapReaderLineHeight(record.lineHeight);
  if (fontSize === null || lineHeight === null) return null;

  const extra: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(record)) {
    if (KNOWN_SETTINGS_KEYS.has(key)) continue;
    extra[key] = entry;
  }

  return {
    version,
    theme: record.theme,
    fontSize,
    lineHeight,
    extra,
  };
}

export function serializeReaderDisplaySettings(
  settings: ReaderDisplaySettings,
): Record<string, unknown> {
  return {
    ...settings.extra,
    version: settings.version,
    theme: settings.theme,
    fontSize: settings.fontSize,
    lineHeight: settings.lineHeight,
  };
}

export type ProfileReaderSettingsRead =
  | { status: 'present'; settings: ReaderDisplaySettings }
  | { status: 'empty' }
  | { status: 'unavailable' };

export type AuthenticatedSettingsDecision = {
  settings: ReaderDisplaySettings;
  /** Valid guest settings to copy once into a confirmed-empty profile. */
  seed: ReaderDisplaySettings | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Read reader settings from a successful profile payload.
 * Missing/null means the profile is confirmed empty.
 * A present but invalid value is unavailable so defaults are not written over it.
 */
export function readReaderSettingsFromProfileBody(body: unknown): ProfileReaderSettingsRead {
  if (!isRecord(body)) return { status: 'unavailable' };

  const user = isRecord(body.user) ? body.user : body;
  if (!isRecord(user)) return { status: 'unavailable' };
  if (!('readerSettings' in user) || user.readerSettings == null) {
    return { status: 'empty' };
  }

  const settings = parseReaderDisplaySettings(user.readerSettings);
  if (!settings) return { status: 'unavailable' };
  return { status: 'present', settings };
}

export function classifyProfileSettingsHttpStatus(status: number): 'ok' | 'unavailable' {
  if (status >= 200 && status < 300) return 'ok';
  return 'unavailable';
}

/**
 * Profile always wins. Seed the guest cookie only when the profile is confirmed empty.
 * Timeouts, 5xx and network failures stay unavailable and must not be overwritten with defaults.
 */
export function resolveAuthenticatedReaderSettings({
  profile,
  cookie,
}: {
  profile: ProfileReaderSettingsRead;
  cookie: ReaderDisplaySettings | null;
}): AuthenticatedSettingsDecision {
  if (profile.status === 'present') {
    return { settings: profile.settings, seed: null };
  }

  if (profile.status === 'unavailable') {
    return { settings: cookie ?? DEFAULT_READER_DISPLAY_SETTINGS, seed: null };
  }

  if (cookie) {
    return { settings: cookie, seed: cookie };
  }

  return { settings: DEFAULT_READER_DISPLAY_SETTINGS, seed: null };
}
